import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

function rowLabel(r) {
  if (r.status === 'rejected') return 'Rejected';
  if (r.status === 'pending') return 'Pending';
  if (r.status === 'approved' && r.submitter_marked_added_at) return 'Approved+Added';
  if (r.status === 'approved') return 'Approved';
  return String(r.status || '');
}

function rowColor(r) {
  if (r.status === 'rejected') return 'error';
  if (r.status === 'pending') return 'warning';
  if (r.submitter_marked_added_at) return 'success';
  if (r.status === 'approved') return 'info';
  return 'default';
}

const MyAccountStatus = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounts/my-duplicate-requests');
      setRows(res.data?.data?.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load your requests');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (r) => {
    const code = r.createdAccount?.account_code;
    setBusyId(r.id);
    try {
      await api.post(`/accounts/my-duplicate-requests/${r.id}/mark-added`);
      toast.success('Marked as added. Opening Accounts to search the new account.');
      const q = code || r.requested_company_name || '';
      navigate(`/accounts?search=${encodeURIComponent(q)}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not mark as added');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            My account status
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Requests you submitted when a new account matched an existing company name. After approval, use <strong>Add</strong> to confirm
            the new account is in your list, then the status shows <strong>Approved+Added</strong>.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} variant="outlined">
          Refresh
        </Button>
      </Stack>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Requested name</TableCell>
                <TableCell>Existing account (duplicate of)</TableCell>
                <TableCell>New account (if approved)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const canAdd = r.status === 'approved' && r.createdAccount && !r.submitter_marked_added_at;
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.requested_company_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.existingAccount?.company_name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" fontFamily="monospace">
                        Ref: {r.existingAccount?.account_code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {r.createdAccount ? (
                        <>
                          <Typography variant="body2">{r.createdAccount.company_name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block" fontFamily="monospace">
                            Ref: {r.createdAccount.account_code}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={rowLabel(r)} color={rowColor(r)} />
                    </TableCell>
                    <TableCell align="right">
                      {canAdd ? (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleAdd(r)}
                          disabled={busyId === r.id}
                        >
                          Add
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {!loading && rows.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>You have not submitted any duplicate account requests yet.</Alert>
      ) : null}
    </Box>
  );
};

export default MyAccountStatus;
